import type { Booking } from '@brighterly/lib-core-types/booking/interfaces'

import { useCustomerStore } from '@stores/me/customer'
import { useBookingStore } from '@stores/booking'
import { useDisciplineStore } from '@stores/discipline'

import { useCsRoutes } from '@composables/routes/cs-route.hook'

import amplitudeConfig from '@config/amplitude.json'

import { trackEvent } from '@tools/amplitude'

interface CurrentBookingApiPayload {
  background: boolean
}

interface CurrentBookingApiOptions {
  onStart?: () => void
  onError?: (err: any) => void
  onSuccess?: (booking: Booking | null) => void
  onFinish?: () => void
}

export const useCurrentBookingApi = async (payload: CurrentBookingApiPayload, options?: CurrentBookingApiOptions): Promise<null | Booking> => {
  const customerStore = useCustomerStore()
  const disciplineStore = useDisciplineStore()
  const bookingStore = useBookingStore()

  const disciplineId = disciplineStore.activeDiscipline?.id || null

  const setStart = (): void => {
    options?.onStart?.()

    if (!payload.background) {
      bookingStore.setLoading(true)
      bookingStore.setCurrentBooking(null)
      bookingStore.setCountdown(null)
    } else {
      if (!bookingStore.isLoadingDone) {
        bookingStore.setLoading(true)
        bookingStore.setCurrentBooking(null)
        bookingStore.setCountdown(null)
      }
    }
  }

  const setSuccess = (booking: Booking | null): void => {
    bookingStore.setCurrentBooking(booking)
    bookingStore.setLoading(false)
    bookingStore.setLoadingDone(true)
    bookingStore.setBookingTrigger()

    options?.onSuccess?.(booking)
  }

  const setError = (err: any): void => {
    bookingStore.setCurrentBooking(null)
    bookingStore.setLoading(false)
    bookingStore.setLoadingDone(false)

    options?.onError?.(err)
  }

  if (!disciplineId) {
    trackEvent(amplitudeConfig.events.requestError, {
      name: 'current_booking',
      message: 'activeDiscipline.id is undefined',
    })

    options?.onError?.(null)
    return null
  } else {
    const { $axios } = useNuxtApp()

    const url = useCsRoutes('bookings.current', {
      '[discipline_id]': disciplineId,
      with_canceled: customerStore.isActiveDisciplinePaid ? 1 : 0,
    })

    setStart()

    try {
      const { data } = await $axios.get(url!)
      const booking = (data?.data || null) as Booking | null

      setSuccess(booking)

      return booking
    } catch (err) {
      setError(err)

      return null
    } finally {
      options?.onFinish?.()
    }
  }
}
